import { useEffect, useMemo, useState } from 'react';
import {
  useTableState,
  useDataViewState,
  UsePaginationReturn,
  DataViewState,
  UseTableStateReturn,
  TableColumn,
  TableBodyCellProps,
  TableHeadCellProps,
  TableProps,
} from '@vtex/admin-ui';
import { NizzaEventStatus } from '@nizza/core';
import { useGetCollection, Product } from '../../../shared';
import { useHighlightProductAction } from './use-highlight-product-action.hook';
import { useGetHighlightProductTableColumns } from './use-get-highlight-product-table-columns.hook';
import { HighlightProductProps } from '../types';

export const pagination = {
  OPTIONS: [5, 7, 8],
  INITIAL_OPTION: 5,
};

type Pagination = {
  OPTIONS: number[];
  INITIAL_OPTION: number;
};

type useHighlightProductTableResult = {
  collectionId: string;
  collectionName: string;
  columns: TableColumn<Product>[];
  isLiveEvent: boolean;
  itemsPerPage: number;
  numberOfItems: number;
  pagination: Pagination;
  products: Product[];
  selectedProductTableData: Product[];
  selectedProducts: string[];
  tableData: Product[];
  view: DataViewState;
  getBodyCell: (
    column: TableColumn<Product, any>,
    item: Product,
  ) => TableBodyCellProps<Product>;
  getHeadCell: (
    column: TableColumn<Product, any>,
  ) => TableHeadCellProps<Product>;
  getTable: () => TableProps<Product>;
  handlePageChange: (pagination: UsePaginationReturn) => void;
  handleSearch: (debouncedValue: string) => void;
  updateItemsPerPage: (value: number | null) => void;
};

export const useHighlightProductTable = (
  props: HighlightProductProps,
): useHighlightProductTableResult => {
  const { collectionId } = props;
  const {
    products,
    name: collectionName,
    loading,
  } = useGetCollection({ collectionId });
  const [paginatedItems, setPaginatedItems] = useState<Product[]>(products);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    pagination.INITIAL_OPTION,
  );

  const numberOfItems = useMemo<number>(() => products.length, [products]);
  const isLiveEvent = useMemo(
    () => props.status === NizzaEventStatus.LIVE,
    [props.status],
  );

  const { onSwitch } = useHighlightProductAction({
    ...props,
    selectedProducts,
    setSelectedProducts,
  });

  const { columns } = useGetHighlightProductTableColumns({
    isLiveEvent,
    onSwitch,
    products,
    selectedProducts,
  });

  const view: DataViewState = useDataViewState();

  const updatePaginatedItems = (start: number, end: number) => {
    setPaginatedItems(products.slice(start, end));
  };

  const handleSearch = (debouncedValue: string) => {
    if (!debouncedValue) return updatePaginatedItems(0, itemsPerPage);

    const textToFound = debouncedValue.toLocaleLowerCase();

    const foundedItems: Product[] = products.filter(
      item =>
        item.name.toLowerCase().includes(textToFound) ||
        item.sku.toString().includes(textToFound),
    );

    setPaginatedItems(foundedItems);
  };

  const handlePageChange = (pagination: UsePaginationReturn) => {
    updatePaginatedItems(pagination.range[0] - 1, pagination.range[1]);
  };

  const updateItemsPerPage = (value: number | null) => {
    setItemsPerPage(value ?? itemsPerPage);
  };

  const changeViewStatus = (status: 'ready' | 'loading' | 'empty') => {
    view.setStatus({
      type: status,
    });
  };

  const {
    data: tableData,
    getBodyCell,
    getHeadCell,
    getTable,
  }: UseTableStateReturn<Product> = useTableState<Product>({
    status: view.status,
    columns,
    items: paginatedItems,
    length: itemsPerPage,
  });

  const { data: selectedProductTableState }: UseTableStateReturn<Product> =
    useTableState<Product>({
      columns,
      items: products,
    });

  const selectedProductTableData = selectedProductTableState.filter(row =>
    selectedProducts.find(id => id === row.id),
  );

  useEffect(() => {
    if (loading) {
      changeViewStatus('loading');
      return;
    }
    if (!products.length) {
      changeViewStatus('empty');
      return;
    }
    updatePaginatedItems(0, itemsPerPage);
    changeViewStatus('ready');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, loading]);

  return {
    collectionId,
    collectionName,
    columns,
    isLiveEvent,
    itemsPerPage,
    numberOfItems,
    pagination,
    products,
    selectedProductTableData,
    selectedProducts,
    tableData,
    view,
    getBodyCell,
    getHeadCell,
    getTable,
    handlePageChange,
    handleSearch,
    updateItemsPerPage,
  };
};
